import type {
  ChannexAriStatus,
  ChannexHealthSignal,
  ChannexHealthState,
  ChannexRentalHealth,
} from '@interfaces/channex.interface';

/**
 * Decides which of the two shapes a rental gets, so both the connected and the unconnected branch
 * can be seen without editing the mock. A rental whose id ends in an even character code has no
 * Channex mapping, the way most rentals in a real account do not.
 */
function mapped(rentalId: string): boolean {
  const last = rentalId.charCodeAt(rentalId.length - 1);
  return Number.isNaN(last) ? true : last % 2 === 1;
}

function hoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 3600_000).toISOString();
}

function signal(
  component: ChannexHealthSignal['component'],
  status: ChannexHealthState,
  extra: Partial<ChannexHealthSignal> = {}
): ChannexHealthSignal {
  return {
    component,
    required: true,
    enabled: true,
    status,
    stale: false,
    observedAt: hoursAgo(0.2),
    lastSuccessAt: hoursAgo(1),
    pendingCount: null,
    failedCount: null,
    reasonCodes: [],
    summary: null,
    ...extra,
  };
}

/**
 * Backend: GET /channex/rentals/{rentalId}/health. Answers for every rental, mapped or not — an
 * unmapped one comes back with a null `propertyId` and `DISABLED`, which is how the calendar tells
 * "not connected" from "connected and failing".
 */
export function getChannexHealthMock(rentalId: string): ChannexRentalHealth {
  if (!mapped(rentalId)) {
    return {
      rentalId,
      propertyId: null,
      overallStatus: 'DISABLED',
      observedAt: hoursAgo(0.2),
      activeConnectionCount: 0,
      signals: {
        connectivity: null,
        credentials: null,
        apiScope: null,
        contentMapping: null,
        channelConnections: null,
        ariAvailability: null,
        ariRestrictions: null,
        bookingFeed: null,
        bookingPoller: null,
        bookingApply: null,
        bookingAck: null,
        bookingCrs: null,
        webhook: null,
      },
      connections: [],
    };
  }

  return {
    rentalId,
    propertyId: 'mock-property-1',
    overallStatus: 'DEGRADED',
    observedAt: hoursAgo(0.2),
    activeConnectionCount: 2,
    signals: {
      connectivity: signal('CONNECTIVITY', 'UP'),
      credentials: signal('CREDENTIALS', 'UP'),
      apiScope: signal('API_SCOPE', 'UP'),
      contentMapping: signal('CONTENT_MAPPING', 'UP'),
      channelConnections: signal('CHANNEL_CONNECTIONS', 'UP'),
      ariAvailability: signal('ARI_AVAILABILITY', 'DEGRADED', {
        pendingCount: 4,
        failedCount: 2,
        reasonCodes: ['ARI_RETRY_WAIT'],
        summary: 'Reintentando el envío de disponibilidad',
      }),
      ariRestrictions: signal('ARI_RESTRICTIONS', 'UP', { pendingCount: 0, failedCount: 0 }),
      bookingFeed: signal('BOOKING_FEED', 'UP'),
      bookingPoller: signal('BOOKING_POLLER', 'UP'),
      bookingApply: signal('BOOKING_APPLY', 'UP'),
      bookingAck: signal('BOOKING_ACK', 'UP'),
      bookingCrs: signal('BOOKING_CRS', 'UNKNOWN'),
      webhook: signal('WEBHOOK', 'UP'),
    },
    connections: [
      {
        connectionId: 'mock-connection-bdc',
        adapterCode: 'BDC',
        active: true,
        health: 'UP',
        stale: false,
        observedAt: hoursAgo(0.2),
        issueCodes: [],
      },
      {
        connectionId: 'mock-connection-airbnb',
        adapterCode: 'AIRBNB',
        active: true,
        health: 'DEGRADED',
        stale: false,
        observedAt: hoursAgo(0.5),
        issueCodes: ['CHANNEL_RATE_PLAN_MISMATCH'],
      },
    ],
  };
}

/**
 * Backend: GET /channex/rentals/{rentalId}/ari/status. Counters are for the whole rental, never for
 * the month on screen. An unmapped rental gets a 404 from the real backend, so the mock refuses the
 * same way rather than inventing a reading the calendar would then show.
 */
export function getChannexAriStatusMock(rentalId: string): ChannexAriStatus | null {
  if (!mapped(rentalId)) {
    return null;
  }
  return {
    rentalId,
    propertyId: 'mock-property-1',
    flowModes: { availability: 'AUTHORITATIVE', restrictions: 'SHADOW' },
    health: 'DEGRADED',
    pendingJobs: 3,
    inFlightJobs: 1,
    verifyingJobs: 0,
    verifiedJobs: 124,
    retryJobs: 1,
    failedJobs: 2,
    partialWarningJobs: 0,
    unknownOutcomeJobs: 0,
    driftJobs: 0,
    oldestPendingAt: hoursAgo(3),
    lastSentAt: hoursAgo(0.4),
    lastVerifiedAt: hoursAgo(1.2),
    lastErrorCode: 'ARI_REMOTE_REJECTED',
  };
}
